import React, { useState } from 'react';
import {
  EnvironmentOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Event } from '../../modules/SportsAndEventsPage/sportsAndEventsInterface';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { currencySignsObj } from '@skygroup/shared/utils/constants';
import moment from 'moment';
import './eventCard.less';
import { Button, Tooltip } from 'antd';
import useGetGrossPrice from '../../hooks/useGetGrossPrice';
import {
  getDateNotFinalTooltipText,
  getDateTimeBoundariesText,
} from '../../modules/SportsAndEventsPage/utils/eventDateTimeHelpers';

const FEW_TICKETS_THRESHOLD = 5;

interface EventCardProps {
  event: Event;
  showAgentCommission?: boolean;
  handleItemClick?: (id: string) => void;
  compactView?: boolean;
  handleEventClick?: (eventIdentifier: string) => void;
  onEventSelect?: (eventIdentifier: string) => void;
  isDynamicPackage?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  showAgentCommission,
  handleItemClick,
  compactView,
  handleEventClick,
  onEventSelect,
  isDynamicPackage,
}) => {
  const { general: t, sportsAndEvents: sportsAndEvents_t } = useSelector(
    (data: RootState) => data.config.translations
  );
  const location = useLocation();
  // const [searchParams, setSearchParams] = useSearchParams();
  const { siteConfig } = useSelector((data: RootState) => data.config.config);

  const handleCardClick = () => {
    if (handleItemClick) {
      handleItemClick(event.identifier ?? event.id.toString());
    }
  };

  const formatEventDate = () => {
    if (!event.dateOfEvent) return '';

    // Parse the date format from API (DD/MM/YYYY)
    const [day, month, year] = event.dateOfEvent.split('/');
    const eventDate = moment(`${year}-${month}-${day}`);

    if (!eventDate.isValid()) return event.dateOfEvent;

    // Format as "Day, DD Mon YYYY" (e.g., "Sun, 23 Nov 2025")
    const dayName = eventDate.format('ddd');
    const formattedDate = eventDate.format('DD MMM YYYY');

    return `${dayName}, ${formattedDate}`;
  };

  const formatEventTime = () => {
    if (!event.timeOfEvent || event.timeOfEvent === '00:00') {
      return '';
    }
    return event.timeOfEvent;
  };

  const getLocationString = () => {
    const parts = [];
    if (event.city?.name) parts.push(event.city.name);
    if (event.country?.name) parts.push(event.country.name);
    if (event.venue?.name) parts.push(event.venue.name);
    return parts.join(', ');
  };

  const getCategoryBadge = () => {
    if (event.eventType?.name) {
      return event.eventType.name;
    }
    return null;
  };

  const getTournamentBadge = () => {
    if (event.tournament?.name) {
      return event.tournament.name;
    }
    return null;
  };

  const getEventName = () => {
    return event.name || sportsAndEvents_t.noTitle;
  };

  const hasPrice =
    event.minTicketPrice?.price !== null &&
    event.minTicketPrice?.price > 0 &&
    event.minTicketPrice?.price !== undefined;
  const currency = event.minTicketPrice?.currency;
  const currencySign =
    currencySignsObj?.[
      currency?.toLowerCase() as keyof typeof currencySignsObj
    ] || currency;

  const eventUrl = `/events/${event.identifier}${location.search}`;

  // TODO: Decide if we need to return the event url-params as string and navigate to it with Link component
  // or update search params with button onclick callback and navigate to it with button onclick callback
  const getEventUrl = () => {
    if (onEventSelect) {
      onEventSelect(event.identifier);
      return window.location.search + '&eTicket=' + event.identifier;
    } else {
      return eventUrl;
    }
  };

  const getGrossPrice = useGetGrossPrice();

  const isMoveToOrderDisabled =
    !event.identifier || event.availableCategoriesQuantity === 0 || !hasPrice;

  const getButtonText = () => {
    if (hasPrice && event.availableCategoriesQuantity > 0) {
      return sportsAndEvents_t.selectTickets;
    } else if (hasPrice && event.availableCategoriesQuantity === 0) {
      return sportsAndEvents_t.noTicketsAvailable;
    } else {
      return sportsAndEvents_t.comingSoon;
    }
  };

  const dateTimeBoundariesText = getDateTimeBoundariesText(event);
  const dateOrTimeNotFinal =
    event.finalDate === false || event.finalTime === false;
  const dateNotFinalTooltipTitle = getDateNotFinalTooltipText(event, {
    dateAndTimeAreSubjectToChange:
      sportsAndEvents_t.dateAndTimeAreSubjectToChange,
    dateIsSubjectToChange: sportsAndEvents_t.dateIsSubjectToChange,
    timeIsSubjectToChange: sportsAndEvents_t.timeIsSubjectToChange,
  });

  return (
    // Removed Link wrapper - navigation now only happens from the button
    // <Link to={eventUrl} className="event-card-link">
    <div className="event-card" onClick={handleCardClick}>
      <div className="badges-container">
        {/* Category Badge */}
        {getCategoryBadge() && (
          <div className="category">{getCategoryBadge()}</div>
        )}

        {/* Tournament Badge */}
        {getTournamentBadge() && (
          <div className="tournament">{getTournamentBadge()}</div>
        )}
      </div>

      {/* Event Name */}
      <h3 className="title">{getEventName()}</h3>

      {/* Location */}
      {getLocationString() && (
        <div className="location">
          <EnvironmentOutlined />
          <span>{getLocationString()}</span>
        </div>
      )}

      {(formatEventDate() || formatEventTime()) && (
        <div className="date-time-container">
          {/* Date */}
          {formatEventDate() && (
            <div className="date">
              <CalendarOutlined />
              <span>{formatEventDate()}</span>
            </div>
          )}

          {/* Time */}
          {formatEventTime() && (
            <div className="time">
              <ClockCircleOutlined />
              <span>{formatEventTime()}</span>
            </div>
          )}
        </div>
      )}

      {dateTimeBoundariesText && (
        <div className="date-time-boundaries">{dateTimeBoundariesText}</div>
      )}

      {/* Date Not Final Hint */}
      {dateOrTimeNotFinal && (
        <Tooltip
          className="date not-final-tooltip"
          title={dateNotFinalTooltipTitle}
        >
          <InfoCircleOutlined />
          <span>{sportsAndEvents_t.dateNotFinal}</span>
        </Tooltip>
      )}

      {hasPrice && !!event.availableCategoriesQuantity &&
        event.availableCategoriesQuantity < FEW_TICKETS_THRESHOLD && (
          <div className="few-tickets-left-row">
            <ClockCircleOutlined />
            <span>{sportsAndEvents_t.onlyAFewTicketsLeft}</span>
          </div>
        )}

      {/* Footer with Price/Status and Button side by side */}
      <div className="footer">
        {hasPrice ? (
          <div className="price">
            <span className="price-label">{t.from}</span>
            <span className="price-amount">
              {currencySign}{' '}
              {getGrossPrice(
                Number(event.minTicketPrice.price),
                isDynamicPackage ? siteConfig?.shareB2C?.dp : event.agent_additional_info?.suggested_commission,
                0
              )}
            </span>
          </div>
        ) : (
          <div className="status-badge" style={{ visibility: 'hidden' }}>
            {/* {sportsAndEvents_t.noTicketsAvailable} */}
          </div>
        )}

        {/* To Order Button - Ant Design button similar to hotel card */}
        {handleEventClick || isMoveToOrderDisabled ? (
          <Button
            disabled={isMoveToOrderDisabled}
            className="btn-style-5"
            type="primary"
            onClick={(e) =>
              !isMoveToOrderDisabled &&
              handleEventClick &&
              event.identifier &&
              handleEventClick(event.identifier)
            }
          >
            {getButtonText()}
          </Button>
        ) : (
          <Link
            className="ant-btn ant-btn-default btn-style-5 order-button"
            to={getEventUrl()}
          >
            {t.toOrder}
          </Link>
        )}
      </div>
    </div>
    // </Link>
  );
};

export default EventCard;
